import { useEffect, useMemo, useRef, useState } from 'react';
import { SALINITY_UNITS, readSalinityUnit, writeSalinityUnit } from '../salinity.js';
import {
  toFormValue,
  formFieldToStored,
  settingsUnitLabel,
  storedBandWarning,
} from '../param-settings.js';
import { api } from '../api.js';
import { effectiveParams } from '../params.js';
import { useConfirm } from './ConfirmDialog.jsx';
import {
  testCardsPatch,
  CYCLE_TESTS,
  isQuarantineTank,
  neverAlerts,
} from '../../../shared/tank-purpose.js';
import { isCyclingPhase } from '../first-run.js';
import { isOwnParam } from '../../../shared/own-param.js';

// What keepers type for the two nitrogen tests. A custom card under one of
// these keys would be charted but never recognised by alerts, Siri or the
// cycle check, which read `ammonia` and `nitrite`.
const NITROGEN_ALIASES = { nh3: 'ammonia', nh4: 'ammonia', nh3nh4: 'ammonia', no2: 'nitrite' };
const nitrogenAlias = (key, label) =>
  NITROGEN_ALIASES[key.replace(/_/g, '')] ||
  (/^\s*ammonia\s*$/i.test(label) && key !== 'ammonia' ? 'ammonia' : null) ||
  (/^\s*nitrite\s*$/i.test(label) && key !== 'nitrite' ? 'nitrite' : null);

// Per-tank parameter overrides (target, acceptable range, hidden,
// custom) + the "reset to starting ranges" button. Was inline in
// TankSettingsSheet; extracted to keep that file as a layout shell.

// Temperature and salinity show in the keeper's units (param-settings.js):
// a Celsius keeper's 24 / 26 used to be saved as °F.
function draftFor(r, ctx) {
  return {
    target: toFormValue(r.key, r.target, ctx),
    low: toFormValue(r.key, r.acceptable?.[0], ctx),
    high: toFormValue(r.key, r.acceptable?.[1], ctx),
    hidden: !!r.hidden,
    label: r.label,
    unit: r.unit ?? '',
  };
}

// `focusKey`: opened from a card's Edit on Home, that card's row is scrolled
// to and marked.
export function ParametersSection({ tank, onReload, onClose, focusKey = null }) {
  const confirm = useConfirm();
  // Show hidden params here so the user can unhide them; filter on Home.
  const rows = useMemo(() => effectiveParams(tank, { includeHidden: true }), [tank]);
  const [salinityUnit, setSalinityUnit] = useState(readSalinityUnit);
  const ctx = { tank, salinityUnit };
  const [drafts, setDrafts] = useState(() =>
    Object.fromEntries(rows.map((r) => [r.key, draftFor(r, ctx)]))
  );
  // Cards added while this is open need drafts before they can be edited.
  useEffect(() => {
    setDrafts((d) => {
      const missing = rows.filter((r) => !d[r.key]);
      if (!missing.length) return d;
      return {
        ...d,
        ...Object.fromEntries(missing.map((r) => [r.key, draftFor(r, { tank, salinityUnit })])),
      };
    });
  }, [rows, tank, salinityUnit]);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState('');
  const [adding, setAdding] = useState(false);
  // The delete choice for a keeper's own parameter: { key, readings } once
  // the count is in (feedback #66: delete it completely, or keep readings).
  const [deleting, setDeleting] = useState(null);
  // An error from a row's own action shows in that row: opened from a
  // card's Edit, the section's top is far off screen.
  const [rowErr, setRowErr] = useState(null);
  const focusRef = useRef(null);
  useEffect(() => {
    focusRef.current?.scrollIntoView?.({ block: 'center' });
  }, []);
  const [newP, setNewP] = useState({ key: '', label: '', unit: '', target: '', low: '', high: '' });

  const setField = (paramKey, field, value) => {
    setDrafts((d) => ({ ...d, [paramKey]: { ...d[paramKey], [field]: value } }));
  };

  const saveRow = async (paramKey) => {
    const d = drafts[paramKey];
    const patch = {};
    // Salinity is stored in ppt and temperature in °F, always. A keeper
    // typing an S.G. value (1.026) here used to store it raw, which later
    // poisoned the salt-mixing math with a 1.026/35 scale (review finding),
    // and a Celsius keeper's 24 / 26 went in as °F.
    // Fields the keeper did not touch go back as stored (°F, ppt), not
    // converted from their rounded display text.
    const row = rows.find((r) => r.key === paramKey);
    const target = formFieldToStored(paramKey, d.target, row?.target, ctx);
    if (target != null) patch.target = target;
    const lo = formFieldToStored(paramKey, d.low, row?.acceptable?.[0], ctx);
    const hi = formFieldToStored(paramKey, d.high, row?.acceptable?.[1], ctx);
    if (lo != null && hi != null) patch.acceptable = [lo, hi];
    patch.hidden = !!d.hidden;
    // The keeper's own parameter takes a new name and unit (feedback #66);
    // a built-in test keeps the ones TankSensei reads it by.
    setRowErr(null);
    if (isOwnParam(paramKey, tank.params?.[paramKey], tank.subtype_slug)) {
      const label = String(d.label || '').trim();
      if (!label) {
        setRowErr({ key: paramKey, msg: 'Give it a name.' });
        return;
      }
      // The same trap as adding one: a card named Ammonia under another key
      // is never recognised by alerts, Siri or the cycle check.
      const alias = nitrogenAlias(paramKey, label);
      if (alias) {
        const name = alias === 'ammonia' ? 'Ammonia' : 'Nitrite';
        setRowErr({
          key: paramKey,
          msg: `Use "+ ${name}" instead, so alerts and Siri know it is ${name.toLowerCase()}.`,
        });
        return;
      }
      const unit = String(d.unit || '')
        .trim()
        .slice(0, 16);
      // Readings keep their numbers: ppm to ppb would read old ones 1000x off.
      const oldUnit = String(row?.unit || '').trim();
      if (oldUnit && unit !== oldUnit) {
        const ok = await confirm({
          title: `Change the unit to ${unit || 'none'}?`,
          body: `Readings already logged keep their numbers. They are not converted from ${oldUnit}.`,
          confirmLabel: 'Change the unit',
        });
        if (!ok) return;
      }
      patch.label = label.slice(0, 40);
      patch.unit = unit;
    }
    setBusy(paramKey);
    setErr('');
    try {
      await api(`/api/tanks/${tank.id}/params`, {
        method: 'PATCH',
        body: { params: { [paramKey]: patch } },
      });
      await onReload();
    } catch (e) {
      setRowErr({ key: paramKey, msg: e.message || 'Could not save.' });
    } finally {
      setBusy(null);
    }
  };

  const toggleHidden = async (paramKey) => {
    const d = drafts[paramKey];
    const next = !d.hidden;
    setField(paramKey, 'hidden', next);
    setBusy(paramKey);
    setErr('');
    try {
      await api(`/api/tanks/${tank.id}/params`, {
        method: 'PATCH',
        body: { params: { [paramKey]: { hidden: next } } },
      });
      await onReload();
    } catch (e) {
      setErr(e.message || 'Could not save.');
      setField(paramKey, 'hidden', !next); // rollback
    } finally {
      setBusy(null);
    }
  };

  // A keeper's own parameter: ask how much goes, with the count of readings
  // it would take, before anything is deleted.
  const startDelete = async (paramKey) => {
    setBusy(paramKey);
    setRowErr(null);
    try {
      const usage = await api(`/api/tanks/${tank.id}/params/${paramKey}/usage`);
      setDeleting({ key: paramKey, readings: Number(usage?.readings) || 0 });
    } catch (e) {
      setRowErr({ key: paramKey, msg: e.message || 'Could not check its readings.' });
    } finally {
      setBusy(null);
    }
  };
  const finishDelete = async (paramKey, withReadings) => {
    const label = rows.find((r) => r.key === paramKey)?.label || paramKey;
    const n = deleting?.readings || 0;
    if (withReadings) {
      const ok = await confirm({
        title: `Delete ${label} and ${n === 1 ? 'its reading' : `all ${n} readings`}?`,
        body: 'The card and its history are gone for good. This cannot be undone.',
        confirmLabel: 'Delete for good',
        destructive: true,
      });
      if (!ok) return;
    }
    setBusy(paramKey);
    setErr('');
    try {
      await api(`/api/tanks/${tank.id}/params/${paramKey}${withReadings ? '?readings=1' : ''}`, {
        method: 'DELETE',
      });
      setDeleting(null);
      await onReload();
    } catch (e) {
      setRowErr({ key: paramKey, msg: e.message || 'Could not delete.' });
    } finally {
      setBusy(null);
    }
  };

  const deleteRow = async (paramKey) => {
    // A cycling tank (or a QT) follows its cycle on these two cards, and a
    // reading needs a card to be logged.
    const cycleCard =
      CYCLE_TESTS.includes(paramKey) && (isCyclingPhase(tank?.phase) || isQuarantineTank(tank));
    const ok = await confirm({
      title: `Delete ${drafts[paramKey].label}?`,
      body: cycleCard
        ? `Readings already logged stay. Without this card you cannot log ${drafts[paramKey].label.toLowerCase()} here, and Sensei follows the cycle on it. Hide keeps it off Home instead.`
        : 'Readings already logged stay. Only the card is removed.',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    setBusy(paramKey);
    setErr('');
    try {
      await api(`/api/tanks/${tank.id}/params`, {
        method: 'PATCH',
        body: { params: { [paramKey]: null } },
      });
      await onReload();
    } catch (e) {
      setErr(e.message || 'Could not delete.');
    } finally {
      setBusy(null);
    }
  };

  const addCustom = async () => {
    const key = newP.key
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_]/g, '');
    if (!/^[a-z][a-z0-9_]{0,30}$/.test(key)) {
      setErr('Key must start with a letter; letters / digits / underscores only.');
      return;
    }
    if (drafts[key]) {
      setErr(`"${key}" already exists on this tank.`);
      return;
    }
    const alias = nitrogenAlias(key, newP.label);
    if (alias) {
      const name = alias === 'ammonia' ? 'Ammonia' : 'Nitrite';
      setErr(
        drafts[alias]
          ? `This tank already has a ${name} card.`
          : `Use "+ ${name}" above, so alerts and Siri know it is ${name.toLowerCase()}.`
      );
      return;
    }
    const patch = {
      label: newP.label.trim() || key,
      unit: newP.unit.trim(),
      custom: true,
      order: 100 + Object.keys(drafts).length,
    };
    const target = newP.target === '' ? null : Number(newP.target);
    if (target != null && Number.isFinite(target)) patch.target = target;
    const lo = newP.low === '' ? null : Number(newP.low);
    const hi = newP.high === '' ? null : Number(newP.high);
    if (lo != null && hi != null && Number.isFinite(lo) && Number.isFinite(hi))
      patch.acceptable = [lo, hi];

    setBusy(`add:${key}`);
    setErr('');
    try {
      await api(`/api/tanks/${tank.id}/params`, {
        method: 'PATCH',
        body: { params: { [key]: patch } },
      });
      await onReload();
      setAdding(false);
      setNewP({ key: '', label: '', unit: '', target: '', low: '', high: '' });
    } catch (e) {
      setErr(e.message || 'Could not add.');
    } finally {
      setBusy(null);
    }
  };

  // Missing ammonia / nitrite cards, offered as one tap each. A hidden one
  // is already in the list above with its Show button.
  // Not offered on an empty params blob: one saved card would replace the
  // baseline cards this list falls back to.
  const hasParams = !!tank?.params && Object.keys(tank.params).length > 0;
  const commonPatch = hasParams ? testCardsPatch(tank.params) : {};
  const addCommon = async (key) => {
    setBusy(`common:${key}`);
    setErr('');
    try {
      await api(`/api/tanks/${tank.id}/params`, {
        method: 'PATCH',
        body: { params: { [key]: commonPatch[key] } },
      });
      await onReload();
    } catch (e) {
      setErr(e.message || 'Could not add.');
    } finally {
      setBusy(null);
    }
  };

  const resetDefaults = async () => {
    const ok = await confirm({
      title: 'Reset every card to its starting range?',
      body: 'Readings already logged stay. Your own cards, and every target and range you changed, are lost.',
      confirmLabel: 'Reset',
      destructive: true,
    });
    if (!ok) return;
    setBusy('reset');
    setErr('');
    try {
      await api(`/api/tanks/${tank.id}/params/reset`, { method: 'POST' });
      await onReload();
      onClose?.();
    } catch (e) {
      setErr(e.message || 'Could not reset.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      {err && (
        <div role="alert" className="ts-err">
          {err}
        </div>
      )}
      <section className="ts-section">
        <div className="ts-section-head">
          <h3 className="font-heading">Parameters</h3>
          <p className="ts-hint">
            Targets and acceptable ranges. Hidden ones disappear from Home but readings survive.
          </p>
        </div>

        <ul className="ts-list">
          {rows.map((r) => {
            // Until the effect above catches up with a just-added card.
            const d = drafts[r.key] || draftFor(r);
            const isBusy = busy === r.key;
            const isCustom = r.custom;
            // Ammonia and nitrite are added as custom cards, but they are
            // the standard tests, not the keeper's own.
            const showCustomTag = isCustom && !CYCLE_TESTS.includes(r.key);
            const bandWarning = storedBandWarning(r.key, r, ctx);
            const own = isOwnParam(r.key, tank.params?.[r.key], tank.subtype_slug);
            const choosing = deleting?.key === r.key;
            return (
              <li
                key={r.key}
                ref={r.key === focusKey ? focusRef : undefined}
                className={`ts-row ${d.hidden ? 'ts-row-hidden' : ''}${r.key === focusKey ? ' ts-row-focus' : ''}`}
              >
                <div className="ts-row-head">
                  <div
                    className="font-heading"
                    style={{ fontSize: 13, letterSpacing: '0.12em', textTransform: 'uppercase' }}
                  >
                    {d.label}
                    <span
                      style={{
                        marginLeft: 6,
                        fontSize: 11,
                        color: 'var(--text-dim)',
                        letterSpacing: 0,
                        textTransform: 'none',
                      }}
                    >
                      {settingsUnitLabel(r.key, d.unit, ctx) || '—'}
                      {showCustomTag ? ' · custom' : ''}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button
                      type="button"
                      onClick={() => toggleHidden(r.key)}
                      disabled={isBusy}
                      className="ts-chip"
                      aria-pressed={d.hidden}
                    >
                      {d.hidden ? 'Show' : 'Hide'}
                    </button>
                    {isCustom && (
                      <button
                        type="button"
                        onClick={() => (own ? startDelete(r.key) : deleteRow(r.key))}
                        disabled={isBusy || choosing}
                        className="ts-chip ts-chip-danger"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
                {choosing && (
                  <div className="ts-delete-choice" role="group" aria-label={`Delete ${d.label}`}>
                    <p className="ts-hint">
                      {deleting.readings
                        ? deleting.readings === 1
                          ? `${r.label} has 1 reading. Keep it, or delete it with the card? A kept reading comes back if you add a card with the same key again.`
                          : `${r.label} has ${deleting.readings} readings. Keep them, or delete them with the card? Kept readings come back if you add a card with the same key again.`
                        : `${r.label} has no readings. Delete the card?`}
                    </p>
                    {deleting.readings > 0 && (
                      <button
                        type="button"
                        className="ts-chip"
                        disabled={isBusy}
                        onClick={() => finishDelete(r.key, false)}
                      >
                        {deleting.readings === 1
                          ? 'Delete the card, keep the reading'
                          : 'Delete the card, keep the readings'}
                      </button>
                    )}
                    <button
                      type="button"
                      className="ts-chip ts-chip-danger"
                      disabled={isBusy}
                      onClick={() => finishDelete(r.key, deleting.readings > 0)}
                    >
                      {deleting.readings > 0
                        ? `Delete the card and ${deleting.readings === 1 ? 'its reading' : `all ${deleting.readings} readings`}`
                        : 'Delete the card'}
                    </button>
                    <button
                      type="button"
                      className="ts-chip"
                      disabled={isBusy}
                      onClick={() => setDeleting(null)}
                    >
                      Cancel
                    </button>
                  </div>
                )}
                {rowErr?.key === r.key && (
                  <div role="alert" className="ts-err" style={{ marginBottom: 8 }}>
                    {rowErr.msg}
                  </div>
                )}
                {own && (
                  <div className="ts-row-body" style={{ marginBottom: 10 }}>
                    <label className="ts-field ts-field-wide">
                      <span>Name</span>
                      <input
                        type="text"
                        maxLength={40}
                        value={d.label}
                        onChange={(e) => setField(r.key, 'label', e.target.value)}
                      />
                    </label>
                    <label className="ts-field">
                      <span>Unit</span>
                      <input
                        type="text"
                        maxLength={16}
                        value={d.unit}
                        onChange={(e) => setField(r.key, 'unit', e.target.value)}
                      />
                    </label>
                  </div>
                )}
                <div className="ts-row-body">
                  <label className="ts-field">
                    <span>Target</span>
                    <input
                      type="number"
                      step="any"
                      inputMode="decimal"
                      value={d.target}
                      onChange={(e) => setField(r.key, 'target', e.target.value)}
                    />
                  </label>
                  <label className="ts-field">
                    <span>Low</span>
                    <input
                      type="number"
                      step="any"
                      inputMode="decimal"
                      value={d.low}
                      onChange={(e) => setField(r.key, 'low', e.target.value)}
                    />
                  </label>
                  <label className="ts-field">
                    <span>High</span>
                    <input
                      type="number"
                      step="any"
                      inputMode="decimal"
                      value={d.high}
                      onChange={(e) => setField(r.key, 'high', e.target.value)}
                    />
                  </label>
                  <button
                    type="button"
                    className="ts-save"
                    onClick={() => saveRow(r.key)}
                    disabled={isBusy}
                  >
                    {isBusy ? '…' : 'Save'}
                  </button>
                </div>
                {neverAlerts(r.key) && (
                  <p className="ts-hint" style={{ marginTop: 8 }}>
                    History only: a range here shows HIGH and LOW on its card and in Today. It never
                    sends an alert.
                  </p>
                )}
                {bandWarning && (
                  <div style={{ marginTop: 8 }}>
                    <p className="ts-hint" role="status" style={{ color: 'var(--alert)' }}>
                      {bandWarning.message}
                    </p>
                    {bandWarning.fix && (
                      <button
                        type="button"
                        className="ts-chip"
                        style={{ minHeight: 44, marginTop: 6 }}
                        disabled={isBusy}
                        onClick={() =>
                          setDrafts((dr) => ({
                            ...dr,
                            [r.key]: { ...dr[r.key], ...bandWarning.fix },
                          }))
                        }
                      >
                        {bandWarning.fixLabel}
                      </button>
                    )}
                  </div>
                )}
                {r.key === 'salinity' && (
                  <div className="ts-row-body" style={{ marginTop: 10, alignItems: 'center' }}>
                    <div className="ts-field" style={{ minWidth: 180 }}>
                      <span>Display unit</span>
                      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                        {SALINITY_UNITS.map((u) => (
                          <button
                            key={u.k}
                            type="button"
                            className={`ts-chip${salinityUnit === u.k ? ' ts-chip-active' : ''}`}
                            aria-pressed={salinityUnit === u.k}
                            onClick={() => {
                              writeSalinityUnit(u.k);
                              setSalinityUnit(u.k);
                              setDrafts((dr) => ({
                                ...dr,
                                salinity: {
                                  ...draftFor(r, { tank, salinityUnit: u.k }),
                                  hidden: dr.salinity?.hidden ?? !!r.hidden,
                                },
                              }));
                            }}
                          >
                            {u.label}
                          </button>
                        ))}
                      </div>
                      <p className="ts-hint" style={{ marginTop: 6 }}>
                        How salinity is shown and entered everywhere. Stored in ppt; photos are read
                        on either refractometer scale and converted.
                      </p>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>

        {Object.keys(commonPatch).length > 0 && (
          <div style={{ margin: '12px 0' }}>
            <p className="ts-hint" style={{ marginBottom: 8 }}>
              Test ammonia or nitrite? Add the card, then log it like any other test. A new tank
              uses them to follow its cycle.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {Object.entries(commonPatch).map(([k, card]) => (
                <button
                  key={k}
                  type="button"
                  className="ts-chip"
                  style={{ minHeight: 44 }}
                  disabled={!!busy}
                  onClick={() => addCommon(k)}
                >
                  {busy === `common:${k}` ? 'Adding…' : `+ ${card.label}`}
                </button>
              ))}
            </div>
          </div>
        )}
        {adding ? (
          <div className="ts-add-form">
            <div className="ts-add-head">
              <h4 className="font-heading">Add custom parameter</h4>
              <button type="button" className="ts-chip" onClick={() => setAdding(false)}>
                Cancel
              </button>
            </div>
            <div className="ts-row-body" style={{ marginTop: 10 }}>
              <label className="ts-field">
                <span>Key</span>
                <input
                  type="text"
                  value={newP.key}
                  maxLength={30}
                  onChange={(e) => setNewP((p) => ({ ...p, key: e.target.value }))}
                  placeholder="kh"
                />
              </label>
              <label className="ts-field" style={{ minWidth: 120 }}>
                <span>Label</span>
                <input
                  type="text"
                  value={newP.label}
                  maxLength={40}
                  onChange={(e) => setNewP((p) => ({ ...p, label: e.target.value }))}
                  placeholder="Carbonate hardness"
                />
              </label>
              <label className="ts-field">
                <span>Unit</span>
                <input
                  type="text"
                  value={newP.unit}
                  maxLength={16}
                  onChange={(e) => setNewP((p) => ({ ...p, unit: e.target.value }))}
                  placeholder="°dKH"
                />
              </label>
            </div>
            <div className="ts-row-body">
              <label className="ts-field">
                <span>Target</span>
                <input
                  type="number"
                  step="any"
                  inputMode="decimal"
                  value={newP.target}
                  onChange={(e) => setNewP((p) => ({ ...p, target: e.target.value }))}
                />
              </label>
              <label className="ts-field">
                <span>Low</span>
                <input
                  type="number"
                  step="any"
                  inputMode="decimal"
                  value={newP.low}
                  onChange={(e) => setNewP((p) => ({ ...p, low: e.target.value }))}
                />
              </label>
              <label className="ts-field">
                <span>High</span>
                <input
                  type="number"
                  step="any"
                  inputMode="decimal"
                  value={newP.high}
                  onChange={(e) => setNewP((p) => ({ ...p, high: e.target.value }))}
                />
              </label>
              <button
                type="button"
                className="ts-save"
                onClick={addCustom}
                disabled={busy?.startsWith('add:')}
              >
                {busy?.startsWith('add:') ? '…' : 'Add'}
              </button>
            </div>
          </div>
        ) : (
          <button type="button" className="ts-add-btn" onClick={() => setAdding(true)}>
            + Add custom parameter
          </button>
        )}
      </section>

      <section className="ts-section">
        <button
          type="button"
          onClick={resetDefaults}
          disabled={busy === 'reset'}
          className="ts-reset"
        >
          {busy === 'reset' ? 'Resetting…' : 'Reset to starting ranges'}
        </button>
        <p className="ts-hint" style={{ marginTop: 8 }}>
          Puts every card back to the targets and ranges TankSensei starts this kind of tank with.
        </p>
      </section>
    </>
  );
}
